import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';

const InvoiceTable = ({ products, isEditable, updateProduct, formatCurrency }) => {
  const [packageOptions, setPackageOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch packages from API
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await apiClient.get('/api/packages');
        const packages = response.data;
        
        // Transform packages into the required format
        const options = packages.reduce((acc, pkg) => {
          if (!acc[pkg.detail]) {
            acc[pkg.detail] = {};
          }
          acc[pkg.detail][pkg.year] = {
            rate: pkg.price,
            description: `${pkg.detail} for ${pkg.year} year(s)`,
            hsn: '9983', // Default HSN code for services
            cgst: 0,
            sgst: 0,
            igst: 0
          };
          return acc;
        }, {});
        
        setPackageOptions(options);
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch packages:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // Handle package type change
  const handlePackageChange = (index, value) => {
    updateProduct(index, 'packageType', value);
    // Reset duration when package type changes
    updateProduct(index, 'duration', '');
  };

  // Handle duration change
  const handleDurationChange = (index, value) => {
    updateProduct(index, 'duration', value);
    
    // Update other fields based on selected package and duration
    const selectedPackage = packageOptions[products[index].packageType][value];
    if (selectedPackage) {
      updateProduct(index, 'rate', selectedPackage.rate);
      updateProduct(index, 'description', selectedPackage.description);
      updateProduct(index, 'hsn', selectedPackage.hsn);
      // Update taxes if needed
      updateProduct(index, 'cgst', selectedPackage.cgst);
      updateProduct(index, 'sgst', selectedPackage.sgst);
      updateProduct(index, 'igst', selectedPackage.igst);
    }
  };

  if (loading) return <div>Loading packages...</div>;
  if (error) return <div>Error loading packages: {error}</div>;

  return (
    <table className="invoice-table">
      <thead>
        <tr>
          <th>#</th>
          {isEditable && <th>Package</th>}
          {isEditable && <th>Duration</th>}
          <th>Description</th>
          <th>HSN/SAC</th>
          <th>Qty</th>
          <th>Rate</th>
          <th>Discount</th>
          <th>CGST(9.00%)</th>
          <th>SGST(9.00%)</th>
          <th>IGST(18.00%)</th>
          <th>Total Amount</th>
        </tr>
      </thead>
      <tbody>
        {products.map((product, index) => {
          const taxable = product.qty * product.rate - product.discount;
          const totalTax = product.cgst + product.sgst + product.igst;
          const amount = taxable + totalTax;

          return (
            <tr key={index}>
              <td>{index + 1}</td>

              {/* Package Type */}
              
                {isEditable && (
                <td>
                  <select
                    value={product.packageType}
                    onChange={(e) => handlePackageChange(index, e.target.value)}
                  >
                    <option value="">Select Package</option>
                    {Object.keys(packageOptions).map((pkg) => (
                      <option key={pkg} value={pkg}>{pkg}</option>
                    ))}
                  </select>
                </td>
              )}
              

              {/* Duration */}
              {isEditable && (
                <td>
                  <select
                    value={product.duration}
                    onChange={(e) => handleDurationChange(index, e.target.value)}
                    disabled={!product.packageType}
                  >
                    <option value="">Select Duration</option>
                    {product.packageType && 
                      Object.keys(packageOptions[product.packageType] || {}).map((duration) => (
                        <option key={duration} value={duration}>{duration} year(s)</option>
                      ))
                    }
                  </select>
                </td>
              )}

              {/* Description */}
              <td>{product.description}</td>

              {/* HSN/SAC */}
              <td>
                {isEditable ? (
                  <input
                    type="text"
                    value={product.hsn}
                    onChange={(e) => updateProduct(index, 'hsn', e.target.value)}
                  />
                ) : (
                  product.hsn
                )}
              </td>

              {/* Quantity */}
              <td>
                {isEditable ? (
                  <input
                    type="number"
                    min="1"
                    value={product.qty}
                    onChange={(e) => updateProduct(index, 'qty', parseInt(e.target.value) || 1)}
                  />
                ) : (
                  product.qty
                )}
              </td>

              {/* Rate */}
              <td>{formatCurrency(product.rate)}</td>

              {/* Discount */}
              <td>
                {isEditable ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={product.discountPercentage || 0}
                      onChange={(e) => {
                        const percentage = parseFloat(e.target.value) || 0;
                        updateProduct(index, 'discountPercentage', percentage);
                        // Calculate actual discount amount based on percentage
                        const discountAmount = (product.rate * product.qty * percentage) / 100;
                        updateProduct(index, 'discount', discountAmount);
                      }}
                      style={{ width: '60px', marginRight: '4px' }}
                    />
                    <span>%</span>
                  </div>
                ) : (
                  <>
                    {formatCurrency(product.discount)} 
                    {product.discountPercentage > 0 && (
                      <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '4px' }}>
                        ({product.discountPercentage}%)
                      </span>
                    )}
                  </>
                )}
              </td>

              {/* CGST */}
              <td>{formatCurrency(product.cgst)}</td>

              {/* SGST */}
              <td>{formatCurrency(product.sgst)}</td>

              {/* IGST */}
              <td>{formatCurrency(product.igst)}</td>

              {/* Amount */}
              <td>{formatCurrency(amount)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default InvoiceTable;